/**
 * Entry point.
 *
 * Order matters:
 *  1. Styles must load before first paint so Tailwind classes resolve.
 *  2. i18n initialises synchronously via top-level await; importing it
 *     before rendering guarantees the first render has translations.
 *  3. Stores rehydrate from localStorage via Zustand middleware; the
 *     pre-paint script in index.html has already applied the correct
 *     `dark` / `dir` attributes, so we don't need to wait for React.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import '@/styles/globals.css';
import '@/i18n/config';

// Trigger rehydration side-effects for persisted stores.
import '@/stores/themeStore';
import '@/stores/localeStore';

import { App } from '@/App';
import { bootstrapAuth } from '@/stores/authStore';

// Hydrate auth state from Supabase before first paint. Fire-and-
// forget — `authStore.hydrated` flips to `true` on resolve, and
// `<ProtectedRoute>` shows a spinner until then.
void bootstrapAuth();

// Dev-only escape hatch: expose the API surface + mode on `window`
// so Playwright tests can invoke real-only endpoints (consent, ZK,
// blueprint instantiate, attestation lookup) without first building
// dedicated UI affordances. Tree-shaken out of production builds.
if (import.meta.env.DEV) {
  void import('@/api/client').then(({ api, apiMode }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__gi_api = api;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__gi_apiMode = apiMode();
  });
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('Mount node #root is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// Register the Monitor service worker for installable / offline support.
// Production only — in dev it would shadow Vite's module graph and break HMR.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      /* offline support is best-effort; the app works without it */
    });
  });
}
